import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { S, Icon, BtnPrimary, BtnSecondary, formatCHF } from '../regureStyles';
import usePgmOfferFunctions, { parseBulkPaste } from './usePgmOfferFunctions';
import ClientSelector from './ClientSelector';
import BrokerSelect from './BrokerSelect';
import PaymentFrequencyField from './PaymentFrequencyField';

// Canton codes are stable; labels come from i18n (offers.cantons.<CODE>).
const CANTON_CODES = ['AG','AI','AR','BE','BL','BS','FR','GE','GL','GR','JU','LU','NE','NW','OW','SG','SH','SO','SZ','TG','TI','UR','VD','VS','ZG','ZH'];

const inputStyle = {
  width: '100%',
  padding: '9px 12px',
  background: '#fff',
  border: `1px solid ${S.borderLight}`,
  borderRadius: 8,
  fontSize: 13,
  color: S.textPrimary,
  outline: 'none',
  boxSizing: 'border-box',
};
const labelStyle = { fontSize: 11, fontWeight: 600, color: S.textSecondary, display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: 0.5 };

const SectionTitle = ({ children }) => (
  <div style={{ fontSize: 12, fontWeight: 700, color: S.burgundy, textTransform: 'uppercase', letterSpacing: 0.8, margin: '18px 0 10px' }}>
    {children}
  </div>
);

const Field = ({ label, children, span = 1 }) => (
  <div style={{ gridColumn: `span ${span}`, marginBottom: 10 }}>
    <label style={labelStyle}>{label}</label>
    {children}
  </div>
);

const compactInput = { ...inputStyle, padding: '6px 8px', fontSize: 12 };

const RosterRow = ({ row, idx, salaryCap, isOnlyRow, onChange, onRemove, t }) => (
  <tr style={{ borderBottom: `1px solid ${S.borderLight}` }}>
    <td style={{ padding: '4px 8px', fontSize: 11, color: S.textMuted, textAlign: 'center', width: 28 }}>{idx + 1}</td>
    <td style={{ padding: '4px 6px', minWidth: 110 }}>
      <input value={row.nom} onChange={e => onChange(row.id, 'nom', e.target.value)} style={compactInput} placeholder={t('offers.pgm.colName')} />
    </td>
    <td style={{ padding: '4px 6px', minWidth: 110 }}>
      <input value={row.prenom} onChange={e => onChange(row.id, 'prenom', e.target.value)} style={compactInput} placeholder={t('offers.pgm.colFirstName')} />
    </td>
    <td style={{ padding: '4px 6px', width: 130 }}>
      <input type='date' value={row.dateOfBirth} onChange={e => onChange(row.id, 'dateOfBirth', e.target.value)} style={compactInput} />
    </td>
    <td style={{ padding: '4px 6px', width: 70 }}>
      <select value={row.gender} onChange={e => onChange(row.id, 'gender', e.target.value)} style={{ ...compactInput, cursor: 'pointer' }}>
        <option value=''>—</option>
        <option value='M'>M</option>
        <option value='F'>F</option>
      </select>
    </td>
    <td style={{ padding: '4px 6px', width: 120 }}>
      <input type='number' min='1' step='1' value={row.annualSalary} onChange={e => onChange(row.id, 'annualSalary', e.target.value)} style={{ ...compactInput, textAlign: 'right' }} />
    </td>
    <td style={{ padding: '4px 6px', width: 110, textAlign: 'right', fontSize: 12, fontVariantNumeric: 'tabular-nums', color: row._wasCapped ? S.warning : S.textSecondary, fontWeight: row._wasCapped ? 600 : 400 }}
        title={row._wasCapped ? t('offers.pgm.cappedAt', { value: salaryCap.toLocaleString('de-CH') }) : ''}>
      {row._capped > 0 ? formatCHF(row._capped) : '—'}
    </td>
    <td style={{ padding: '4px 6px', width: 32, textAlign: 'center' }}>
      <button
        onClick={() => onRemove(row.id)}
        disabled={isOnlyRow}
        title={isOnlyRow ? t('offers.pgm.atLeastOneEmp') : t('offers.pgm.delete')}
        style={{ background: 'none', border: 'none', cursor: isOnlyRow ? 'not-allowed' : 'pointer', opacity: isOnlyRow ? 0.3 : 1, padding: 4 }}
      >
        <Icon name='trash' color={S.danger} size={14} />
      </button>
    </td>
  </tr>
);

const BulkPasteBox = ({ onParsed }) => {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [errors, setErrors] = useState([]);
  const [info, setInfo] = useState('');

  const handleParse = () => {
    const { rows, errors: errs } = parseBulkPaste(text);
    setErrors(errs);
    if (errs.length === 0 && rows.length > 0) {
      onParsed(rows);
      setText('');
      setInfo(t('offers.pgm.bulkParsedInfo', { count: rows.length }));
      setTimeout(() => setInfo(''), 2400);
    } else if (rows.length === 0 && errs.length === 0) {
      setInfo(t('offers.pgm.bulkParsePrompt'));
      setTimeout(() => setInfo(''), 3000);
    }
  };

  return (
    <div style={{ background: S.bgCard, border: `1px dashed ${S.borderLight}`, borderRadius: 8, padding: 12, marginBottom: 12 }}>
      <div style={{ fontSize: 11, color: S.textMuted, marginBottom: 6 }}>
        {t('offers.pgm.bulkPasteHint')} <code style={{ background: '#fff', padding: '0 4px', borderRadius: 3 }}>Name &nbsp; [YYYY-MM-DD] &nbsp; [M/F] &nbsp; salary</code> {t('offers.pgm.bulkPasteHintExt')}
      </div>
      <textarea
        value={text}
        onChange={e => setText(e.target.value)}
        rows={4}
        placeholder={'Dupont\tJean\t1985-03-15\tM\t45000\nMartin\tMarie\t1990-07-22\tF\t52000'}
        style={{ ...inputStyle, fontFamily: 'monospace', fontSize: 12, resize: 'vertical', marginBottom: 8 }}
      />
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <BtnSecondary onClick={handleParse} style={{ padding: '6px 12px' }} disabled={!text.trim()}>
          <Icon name='check' color={S.textSecondary} size={13} /> {t('offers.pgm.bulkParseButton')}
        </BtnSecondary>
        {info && <span style={{ fontSize: 12, color: S.success, fontWeight: 600 }}>{info}</span>}
      </div>
      {errors.length > 0 && (
        <div style={{ marginTop: 8, padding: '8px 10px', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 6, fontSize: 11, color: '#991b1b' }}>
          {errors.map(err => <div key={err.line}>{t('offers.pgm.bulkParseLine', { line: err.line, message: err.message })}</div>)}
        </div>
      )}
    </div>
  );
};

const PgmOfferForm = ({ onClose, onCreated, reviseFrom = null, editDraft = null }) => {
  const { t } = useTranslation();
  const {
    rateTable, rateTableLoading, rateTableError,
    salaryCap, salaryCapped, cappedEmployeeNames,
    contractTerms,
    selectedRateRow,
    policyholder, updatePolicyholder,
    clientId, selectClient, clearClient,
    brokerId, setBrokerId,
    productConfig, updateProductConfig,
    employees, totalPayrollPreview,
    updateEmployee, addEmployee, removeEmployee, replaceRoster,
    calc, calcLoading, calcError,
    canCalculate,
    saving, saveError,
    saveDraft, createOffer,
    paymentTerms,
  } = usePgmOfferFunctions({ onClose, onCreated, reviseFrom, editDraft });

  const [bulkMode, setBulkMode] = useState(false);

  // Payment frequency is rendered by the shared PaymentFrequencyField,
  // driven only by the tenant's PGM paymentTerms. It used to be fed
  // `calc.totalPremium`, which PGM's engine does not return (its field is
  // `premium`), so above CHF 10,000 the control still offered Annuel only.
  // Durations come from the tenant's config, in years, and are labelled
  // via i18n pluralisation. Previously a hardcoded list of French display
  // strings ('1 an'), which is why the value stored on the offer could
  // never be rendered in English or German.
  const CONTRACT_DURATIONS = contractTerms.map(y => ({
    value: y,
    label: t('offers.pgm.durationYears', { count: y }),
  }));

  if (rateTableLoading) return <div style={{ padding: 40, fontSize: 12, color: S.textMuted }}>{t('offers.common.loadingRateTable')}</div>;
  if (rateTableError) return (
    <div style={{ padding: '10px 14px', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, fontSize: 13, color: '#991b1b' }}>
      {rateTableError}
    </div>
  );

  const waitingOptions = rateTable?.availableWaitingPeriods || [];
  const coverageOptions = rateTable?.availableCoveragePercents || [];
  // Always render the translated label — the DB stores the canonical French text
  // as a stable config value, but users see the string in their locale.
  const coverageDescription = t('offers.pgm.coverageDescriptionText');
  const provisional = selectedRateRow && !selectedRateRow.confirmed;

  return (
    <>
      <SectionTitle>{t('offers.common.companyData')}</SectionTitle>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label={t('offers.common.client', { defaultValue: 'Client' })} span={2}>
          <ClientSelector value={clientId} onSelect={selectClient} onClear={clearClient} />
        </Field>
        <Field label={t('offers.common.broker', { defaultValue: 'Broker' })} span={2}>
          <BrokerSelect value={brokerId} onChange={setBrokerId} inputStyle={inputStyle} />
        </Field>
        <Field label={t('offers.common.companyName')} span={2}>
          <input value={policyholder.companyName} onChange={e => updatePolicyholder('companyName', e.target.value)} style={inputStyle} />
        </Field>
        <Field label={t('offers.common.address')} span={2}>
          <textarea
            value={policyholder.address}
            onChange={e => updatePolicyholder('address', e.target.value)}
            rows={2}
            style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
          />
        </Field>
        <Field label={t('offers.common.canton')}>
          <select value={policyholder.canton} onChange={e => updatePolicyholder('canton', e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
            <option value=''>{t('offers.cantons.select')}</option>
            {CANTON_CODES.map(code => <option key={code} value={code}>{t(`offers.cantons.${code}`)}</option>)}
          </select>
        </Field>
        <Field label={t('offers.pgm.nogaCode')}>
          <input value={policyholder.nogaCode} onChange={e => updatePolicyholder('nogaCode', e.target.value)} style={inputStyle} />
        </Field>
        <Field label={t('offers.pgm.companyContact')}>
          <input value={policyholder.contactName} onChange={e => updatePolicyholder('contactName', e.target.value)} style={inputStyle} />
        </Field>
        <Field label={t('offers.common.phone')}>
          <input value={policyholder.contactPhone} onChange={e => updatePolicyholder('contactPhone', e.target.value)} style={inputStyle} />
        </Field>
        <Field label={t('offers.common.email')} span={2}>
          <input type='email' value={policyholder.contactEmail} onChange={e => updatePolicyholder('contactEmail', e.target.value)} style={inputStyle} />
        </Field>
      </div>

      <SectionTitle>{t('offers.common.coverage')}</SectionTitle>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label={t('offers.common.waitingPeriod')}>
          <select value={productConfig.waitingPeriod} onChange={e => updateProductConfig('waitingPeriod', e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
            {waitingOptions.map(v => <option key={v} value={v}>{v} {t('offers.common.daysUnit')}</option>)}
          </select>
        </Field>
        <Field label={t('offers.pgm.coveragePercentLabel')}>
          <select value={productConfig.coveragePercent} onChange={e => updateProductConfig('coveragePercent', e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
            {coverageOptions.map(v => <option key={v} value={v}>{v}%</option>)}
          </select>
        </Field>
      </div>
      <div style={{ fontSize: 11, color: S.textMuted, marginBottom: 4, fontStyle: 'italic' }}>
        {t('offers.pgm.coverageBasis', { description: coverageDescription })}
      </div>
      {selectedRateRow && (
        <div style={{ fontSize: 12, color: S.textMuted, marginBottom: 4 }}>
          {t('offers.pgm.applicableRate')}: <strong style={{ fontVariantNumeric: 'tabular-nums', color: S.textPrimary }}>{Number(selectedRateRow.rate).toFixed(3)}%</strong>
        </div>
      )}
      {provisional && (
        <div style={{ padding: '10px 14px', background: 'rgba(245,158,11,0.08)', border: `1px solid ${S.warning}55`, borderRadius: 8, fontSize: 12, color: '#92400e', marginBottom: 8 }}>
          <Icon name='alert-triangle' color={S.warning} size={13} /> {t('offers.pgm.provisionalRate', { rate: Number(selectedRateRow.rate).toFixed(3) })}
        </div>
      )}

      <SectionTitle>{t('offers.pgm.employeeRoster')}</SectionTitle>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: S.textSecondary, cursor: 'pointer' }}>
          <input type='checkbox' checked={bulkMode} onChange={e => setBulkMode(e.target.checked)} />
          {t('offers.pgm.quickEntry')}
        </label>
      </div>
      {bulkMode && <BulkPasteBox onParsed={replaceRoster} />}

      <div style={{ background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 8, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: S.bgCard, borderBottom: `1px solid ${S.borderLight}` }}>
              <th style={{ padding: '8px 6px', fontSize: 10, fontWeight: 700, color: S.textMuted, textTransform: 'uppercase', textAlign: 'center' }}>#</th>
              <th style={{ padding: '8px 8px', fontSize: 10, fontWeight: 700, color: S.textMuted, textTransform: 'uppercase', textAlign: 'left' }}>{t('offers.pgm.colName')}</th>
              <th style={{ padding: '8px 8px', fontSize: 10, fontWeight: 700, color: S.textMuted, textTransform: 'uppercase', textAlign: 'left' }}>{t('offers.pgm.colFirstName')}</th>
              <th style={{ padding: '8px 8px', fontSize: 10, fontWeight: 700, color: S.textMuted, textTransform: 'uppercase', textAlign: 'left' }}>{t('offers.pgm.colDob')}</th>
              <th style={{ padding: '8px 8px', fontSize: 10, fontWeight: 700, color: S.textMuted, textTransform: 'uppercase', textAlign: 'left' }}>{t('offers.pgm.colGender')}</th>
              <th style={{ padding: '8px 8px', fontSize: 10, fontWeight: 700, color: S.textMuted, textTransform: 'uppercase', textAlign: 'right' }}>{t('offers.pgm.colSalary')}</th>
              <th style={{ padding: '8px 8px', fontSize: 10, fontWeight: 700, color: S.textMuted, textTransform: 'uppercase', textAlign: 'right' }}>{t('offers.pgm.colCapped')}</th>
              <th style={{ padding: '8px 6px', width: 32 }}></th>
            </tr>
          </thead>
          <tbody>
            {employees.map((row, i) => (
              <RosterRow
                key={row.id}
                row={row}
                idx={i}
                salaryCap={salaryCap}
                isOnlyRow={employees.length === 1}
                onChange={updateEmployee}
                onRemove={removeEmployee}
                t={t}
              />
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8, marginBottom: 8 }}>
        <BtnSecondary onClick={addEmployee} style={{ padding: '6px 12px' }}>
          <Icon name='plus' color={S.textSecondary} size={13} /> {t('offers.pgm.addEmployee')}
        </BtnSecondary>
        <div style={{ fontSize: 12, color: S.textSecondary }}>
          <span style={{ marginRight: 14 }}>{employees.length} {t('offers.pgm.employeesLabel')}</span>
          <span>{t('offers.pgm.sumCappedSalaries')}: <strong style={{ color: S.textPrimary, fontVariantNumeric: 'tabular-nums' }}>{formatCHF(totalPayrollPreview)}</strong></span>
        </div>
      </div>

      <SectionTitle>{t('offers.common.commercialTerms')}</SectionTitle>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label={t('offers.common.effectiveDate')}>
          <input type='date' value={productConfig.effectiveDate} onChange={e => updateProductConfig('effectiveDate', e.target.value)} style={inputStyle} />
        </Field>
        {/* NO correspondence-recipient selector here, deliberately. The AROMED
            PGM template has no correspondence-address row — the renderer calls
            resolveCorrespondenceBlock only in buildRcProOfferDocument — so a
            selector would ask the operator a question the document never
            answers. Adding the row would mean inventing wording AROMED did not
            ask for.

            The FIELD still exists end to end: pgmController allow-lists
            correspondenceRecipient and it defaults to 'insured', ready for
            document-delivery routing. Only the control is absent.
            See tests/correspondenceRecipientRoundTrip.test.mjs. */}
        <Field label={t('offers.pgm.paymentFrequency')}>
          <PaymentFrequencyField
            terms={paymentTerms}
            premium={calc?.premium}
            value={productConfig.paymentFrequency || paymentTerms.frequency}
            onChange={(v) => updateProductConfig('paymentFrequency', v)}
          />
        </Field>
        <Field label={t('offers.pgm.contractDuration')} span={2}>
          <select
            value={productConfig.contractTermYears ?? contractTerms[0]}
            onChange={e => updateProductConfig('contractTermYears', Number(e.target.value))}
            style={{ ...inputStyle, cursor: 'pointer' }}
          >
            {CONTRACT_DURATIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </Field>
      </div>

      <SectionTitle>{t('offers.common.premiumSummary')}</SectionTitle>
      {calcError && (
        <div style={{ padding: '10px 14px', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, fontSize: 13, color: '#991b1b', marginBottom: 10 }}>
          {calcError}
        </div>
      )}
      {!canCalculate && !calc && (
        <div style={{ padding: 14, color: S.textMuted, fontSize: 12, fontStyle: 'italic' }}>
          {t('offers.pgm.completeAllFields')}
        </div>
      )}
      {canCalculate && calcLoading && !calc && (
        <div style={{ padding: 14, color: S.textMuted, fontSize: 12 }}>{t('offers.pgm.calcInProgress')}</div>
      )}
      {calc && (
        <div style={{ background: 'rgba(128,0,32,0.03)', border: `1px solid ${S.burgundy}30`, borderRadius: 10, padding: 14 }}>
          {!calc.rateConfirmed && (
            <div style={{ padding: '8px 12px', background: 'rgba(245,158,11,0.1)', border: `1px solid ${S.warning}55`, borderRadius: 6, fontSize: 11, color: '#92400e', marginBottom: 10 }}>
              <Icon name='alert-triangle' color={S.warning} size={12} /> {t('offers.pgm.provisionalShort')}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: S.textSecondary, marginBottom: 4 }}>
            <span>{t('offers.pgm.employeeCount')}</span>
            <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{calc.employeeCount}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: S.textSecondary, marginBottom: 4 }}>
            <span>{t('offers.pgm.payrollSum')}</span>
            <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{formatCHF(calc.totalPayroll)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: S.textSecondary, marginBottom: 6 }}>
            <span>{t('offers.common.premiumRate')}</span>
            <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{Number(calc.rate).toFixed(3)}%</span>
          </div>
          <div style={{ borderTop: `1px dashed ${S.borderLight}`, paddingTop: 8 }}>
            {calc.minimumApplied && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: S.textMuted, marginBottom: 4 }}>
                <span>{t('offers.pgm.calculatedPremium')}</span>
                <span style={{ fontVariantNumeric: 'tabular-nums', textDecoration: 'line-through' }}>{formatCHF(calc.rawPremium)}</span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                {calc.minimumApplied ? t('offers.pgm.minPremiumLabel') : t('offers.common.annualPremium')}
              </span>
              <span style={{ fontSize: 22, fontWeight: 800, color: S.burgundy, fontVariantNumeric: 'tabular-nums' }}>{formatCHF(calc.premium)}</span>
            </div>
          </div>
          <div style={{ borderTop: `1px solid ${S.borderLight}`, marginTop: 10, paddingTop: 8, fontSize: 11, color: S.textMuted, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
            <span>{t('offers.pgm.waitingSummary')}: <strong style={{ color: S.textSecondary }}>{calc.inputs.waitingPeriod} {t('offers.common.daysUnit')}</strong></span>
            <span>{t('offers.pgm.coverageSummary')}: <strong style={{ color: S.textSecondary }}>{t('offers.pgm.coverageOfSalary', { percent: calc.inputs.coveragePercent })}</strong></span>
            <span>{t('offers.pgm.maxBenefitDays')}: <strong style={{ color: S.textSecondary }}>{t('offers.pgm.daysPerCase', { days: calc.inputs.maxBenefitDays })}</strong></span>
            <span>{t('offers.pgm.maxSalaryPerPerson')}: <strong style={{ color: S.textSecondary }}>{t('offers.pgm.perPerson', { value: formatCHF(calc.salaryCap) })}</strong></span>
            <span style={{ gridColumn: '1 / -1' }}>{t('offers.pgm.coverageBasisLabel')}: <strong style={{ color: S.textSecondary }}>{coverageDescription}</strong></span>
          </div>
        </div>
      )}

      {salaryCapped && (
        <div style={{ padding: '10px 14px', background: S.surfaceMuted || '#f8fafc', border: `1px solid ${S.borderLight}`, borderRadius: 8, fontSize: 13, color: S.textSecondary, marginBottom: 10 }}>
          {t('offers.pgm.salaryCapNotice', {
            value: formatCHF(salaryCap),
            names: cappedEmployeeNames.join(', '),
            count: cappedEmployeeNames.length,
          })}
        </div>
      )}
      {saveError && (
        <div style={{ padding: '10px 14px', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, fontSize: 13, color: '#991b1b', marginTop: 10 }}>
          {saveError}
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
        <BtnSecondary onClick={onClose} style={{ flex: 1, justifyContent: 'center' }}>
          {t('offers.common.cancel')}
        </BtnSecondary>
        <BtnSecondary onClick={saveDraft} disabled={!calc || saving} style={{ flex: 1, justifyContent: 'center', opacity: (!calc || saving) ? 0.6 : 1 }}>
          {saving ? '…' : t('offers.common.saveDraft')}
        </BtnSecondary>
        <BtnPrimary onClick={createOffer} disabled={!calc || saving} style={{ flex: 1, justifyContent: 'center', opacity: (!calc || saving) ? 0.6 : 1 }}>
          <Icon name='check' color='#fff' size={15} /> {saving ? t('offers.common.creating') : t('offers.common.createOffer')}
        </BtnPrimary>
      </div>
    </>
  );
};

export default PgmOfferForm;
